
import { supabase } from '@/integrations/supabase/client';
import { sendNotification } from '@/utils/sendNotification';
import { CAYMAN_LAUNCH_DATE } from '@/utils/date-constants';
import type { WaitlistNotificationResult } from './types';

/**
 * Trigger waitlist notification for users who haven't been notified yet
 * @param countryCode Optional country code to filter by
 * @param batchSize Number of emails to process in one batch
 * @returns Status of the operation
 */
export const triggerWaitlistNotification = async (
  countryCode?: string,
  batchSize: number = 50
): Promise<WaitlistNotificationResult> => {
  try {
    // Call the notify-waitlist edge function
    const { data, error } = await supabase.functions.invoke('notify-waitlist', {
      body: {
        countryCode,
        batchSize
      }
    });

    if (error) {
      console.error('Error triggering waitlist notification:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception in triggerWaitlistNotification:', error);
    return { success: false, error };
  }
};

/**
 * Send confirmation email to a waitlist subscriber
 * @param email Email address of the recipient
 * @param metadata Additional data for email template
 * @returns Status of the email operation
 */
export const sendWaitlistConfirmationEmail = async (
  email: string,
  metadata: {
    countryCode?: string,
    countryName?: string,
    formattedLaunchDate?: string,
    baseUrl?: string
  }
) => {
  try {
    // Format launch date for email if not provided
    const formattedLaunchDate = metadata.formattedLaunchDate || CAYMAN_LAUNCH_DATE.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    
    // Prepare country name based on country code
    const countryName = metadata.countryName || (metadata.countryCode === 'KY' ? 
      'the Cayman Islands' : 
      (metadata.countryCode || 'Cayman Islands'));
    
    // Send confirmation email
    const result = await sendNotification(
      'waitlistConfirmation',
      email,
      {
        recipientName: 'User', // Add required recipientName field 
        countryCode: metadata.countryCode || 'KY',
        countryName,
        formattedLaunchDate,
        baseUrl: metadata.baseUrl || window.location.origin
      }
    );
    
    if (result.success) {
      console.log('Waitlist confirmation email sent to:', email);
      return { success: true };
    } else {
      console.error('Failed to send waitlist confirmation email:', result.error);
      return { success: false, error: result.error };
    }
  } catch (emailError) {
    // Log the error but don't fail the whole operation
    console.error('Failed to send waitlist confirmation email:', emailError);
    return { success: false, error: emailError };
  }
};
